import type { ProjectEvent } from "@/engine/types";

/**
 * Independently eligible, not scheduled by anyone — day thresholds AND metric
 * thresholds together, which a fixed next-scene chain can't express. May or
 * may not fire in a given playthrough depending on how the project is doing.
 */
export const act3Events: ProjectEvent[] = [
  {
    id: "security_review_delay",
    title: "A Finding in Security Review",
    sender: "Security Architect",
    day: "Day 5+",
    eligibleWhen: (state) => state.day >= 5 && state.schedule < 70,
    body: [
      "\"Your review came back with a finding — the new AWS path needs an additional encryption-at-rest control before we can sign off. This is exactly the kind of thing that gets missed when timelines are already tight.\"",
    ],
    choices: [
      {
        id: "remediate_now",
        text: "Remediate the finding properly before proceeding",
        description: "Fix the control gap, accept the schedule hit.",
        effects: [
          { metric: "schedule", delta: -8 },
          { metric: "quality", delta: 10 },
          { metric: "operationalRisk", delta: -15 },
        ],
        resultText: "The control gets built right. Security closes the finding without conditions.",
      },
      {
        id: "push_back_on_finding",
        text: "Push back on the finding to protect the date",
        description: "Argue it's low-risk, ask for a conditional pass.",
        effects: [
          { metric: "operationalRisk", delta: 20 },
          { metric: "schedule", delta: 5 },
          { metric: "stakeholderTrust", delta: -5 },
        ],
        resultText:
          "Security grants a conditional pass under protest. The gap is now a documented, open risk sitting on the project — not gone, just deferred with your name on the exception.",
      },
    ],
  },
  {
    id: "data_quality_issue",
    title: "UAT Finds Something",
    sender: "BI Developer",
    day: "Day 6+",
    eligibleWhen: (state) => state.day >= 6 && state.quality < 90,
    body: [
      "A clinician reviewing the dashboard flags that encounter counts don't match the legacy system for observation stays — they're being double-counted.",
      "BI developer: \"That's a mapping issue, not a pipeline bug. Maybe 1–2 days to fix, but it touches shared logic three other dashboards will eventually reuse.\"",
    ],
    choices: [
      {
        id: "durable_fix",
        text: "Fix the shared mapping logic at the source",
        description: "Costs more time now, prevents the bug resurfacing in future dashboards.",
        effects: [
          { metric: "schedule", delta: -5 },
          { metric: "quality", delta: 12 },
          { metric: "technicalDebt", delta: -10 },
        ],
        resultText:
          "The team fixes it at the source. It costs a day and a half, but the next dashboards that reuse this pipeline won't inherit the same defect.",
      },
      {
        id: "quick_patch",
        text: "Patch the dashboard output, log a follow-up ticket",
        description: "Protect the date, defer the root cause.",
        effects: [
          { metric: "schedule", delta: 5 },
          { metric: "quality", delta: -12 },
          { metric: "technicalDebt", delta: 15 },
          { metric: "stakeholderTrust", delta: -3 },
        ],
        resultText:
          "You hit the date. The same double-count bug is now baked into shared logic, waiting to reappear in hospital #2's dashboard.",
      },
    ],
  },
  {
    id: "team_burnout_check",
    title: "The Team Is Fraying",
    sender: "Data Engineer",
    day: "Day 8+",
    eligibleWhen: (state) => state.day >= 8 && state.teamTrust < 60,
    body: [
      "\"Can we talk. We've been absorbing every schedule surprise on this project without anyone asking if that's sustainable. A few of us are close to burned out, and PTO keeps getting pushed.\"",
    ],
    choices: [
      {
        id: "protect_the_team",
        text: "Protect the team — adjust scope or timeline, not people",
        description: "Take the schedule hit instead of the team.",
        effects: [
          { metric: "teamTrust", delta: 15 },
          { metric: "schedule", delta: -5 },
        ],
        resultText:
          "The team notices you chose them over the date. Trust recovers, and so does the quality of their work.",
      },
      {
        id: "push_through",
        text: "Ask the team to push through to the current date",
        description: "One more sprint, then things ease up.",
        effects: [
          { metric: "teamTrust", delta: -15 },
          { metric: "quality", delta: -8 },
          { metric: "operationalRisk", delta: 10 },
        ],
        resultText:
          "They push through. The work that comes out the other side has more bugs in it than anything shipped earlier in the project.",
      },
    ],
  },
  {
    id: "budget_overrun_warning",
    title: "AWS Spend Is Trending Over Forecast",
    sender: "Finance Partner",
    day: "Day 10+",
    eligibleWhen: (state) => state.day >= 10 && state.budget < 70,
    body: [
      "\"Your AWS spend is tracking above forecast — looks like duplicate non-prod environments and an autoscaling group that never got tuned down after load testing.\"",
    ],
    choices: [
      {
        id: "rightsizing_pass",
        text: "Run a rightsizing pass before go-live",
        description: "Spend a couple of engineering days cleaning up infrastructure.",
        effects: [
          { metric: "budget", delta: 12 },
          { metric: "schedule", delta: -5 },
          { metric: "technicalDebt", delta: -5 },
        ],
        resultText: "Duplicate environments get torn down, autoscaling gets tuned. Spend comes back in line.",
      },
      {
        id: "defer_to_next_sprint",
        text: "Defer cleanup to after go-live",
        description: "Protect the date, log a follow-up.",
        effects: [
          { metric: "budget", delta: -8 },
          { metric: "operationalRisk", delta: 10 },
        ],
        resultText:
          "Nothing gets cleaned up. The overrun keeps compounding quietly in the background while attention stays on the go-live date.",
      },
    ],
  },
];
